import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { makeQrValue } from '../utils/qrCodes';
import { resolveDisplayNames, saveShortNames } from '../utils/labelNames';
import LabelPreview from './LabelPreview';
import useScrollLock from '../utils/useScrollLock';
import './LabelBatchReviewModal.css';

/*
  LabelBatchReviewModal — revisión editable previa a la generación en lote.

  Muestra una fila por variación seleccionada con vista previa + campos de
  "nombre a mostrar" y "variación a mostrar" acortables. Al confirmar, guarda
  las versiones cortas y devuelve las ediciones al padre para armar el PDF.

  Props:
    open        boolean
    rows        [{ key, productId, variationId, productName, variationLabel,
                   sku, price, loteNumero, qty }]
    storeName   string
    labelConfig { width, height }
    generating  boolean
    onCancel    () => void
    onConfirm   (edits: { [key]: { name, variation } }) => void
*/
export default function LabelBatchReviewModal({
  open, rows, storeName, labelConfig, generating, onCancel, onConfirm,
}) {
  useScrollLock(open);
  const [edits, setEdits] = useState({});

  // Inicializa cada fila con la versión corta guardada (o el nombre completo).
  useEffect(() => {
    if (!open) return;
    const init = {};
    for (const r of rows) {
      const resolved = resolveDisplayNames(r.productId, r.variationId, r.productName, r.variationLabel || '');
      init[r.key] = { name: resolved.name, variation: resolved.variation };
    }
    setEdits(init);
  }, [open, rows]);

  const totalLabels = useMemo(() => rows.reduce((s, r) => s + (r.qty || 0), 0), [rows]);

  const setField = (key, field, value) => {
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const handleConfirm = () => {
    if (generating) return;
    for (const r of rows) {
      const e = edits[r.key] || {};
      saveShortNames(r.productId, r.variationId, {
        name: e.name, variation: e.variation,
        layout: r.layout,
        originalName: r.productName, originalVariation: r.variationLabel || '',
      });
    }
    onConfirm(edits);
  };

  if (!open) return null;

  return createPortal((
    <div className="lbr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="lbr-card" role="dialog" aria-modal="true">
        <div className="lbr-head">
          <div>
            <span className="lbr-title">Revisar etiquetas</span>
            <span className="lbr-sub">
              {rows.length} {rows.length === 1 ? 'producto' : 'productos'} · {totalLabels} {totalLabels === 1 ? 'etiqueta' : 'etiquetas'}
            </span>
          </div>
          <button className="lbr-close" onClick={onCancel} aria-label="Cerrar">✕</button>
        </div>

        <p className="lbr-hint">
          Acortá el nombre o la variación de cada etiqueta para que quepan bien.
          La vista previa se actualiza en tiempo real y los cambios se guardan para la próxima vez.
        </p>

        <div className="lbr-list">
          {rows.map((r) => {
            const e = edits[r.key] || { name: r.productName, variation: r.variationLabel || '' };
            const previewItem = {
              qrValue:        makeQrValue(r.productId, r.variationId),
              productName:    (e.name || '').trim(),
              variationLabel: (e.variation || '').trim(),
              price:          r.price,
              sku:            r.sku,
              loteNumero:     r.loteNumero,
              layout:         r.layout   || null,
              qrScale:        r.qrScale  ?? 1,
              priceSize:      r.priceSize ?? null,
              nameSize:       r.nameSize  ?? null,
              varSize:        r.varSize   ?? null,
              storeName,
            };
            return (
              <div className="lbr-row" key={r.key}>
                <div className="lbr-row__preview">
                  <LabelPreview item={previewItem} config={labelConfig} />
                  {r.qty > 1 && <span className="lbr-row__qty">×{r.qty}</span>}
                </div>
                <div className="lbr-row__fields">
                  <label className="lbr-field">
                    <span className="lbr-field__label">Nombre</span>
                    <input
                      className="lbr-field__input"
                      type="text"
                      value={e.name}
                      onChange={(ev) => setField(r.key, 'name', ev.target.value)}
                      placeholder={r.productName}
                    />
                  </label>
                  {(r.variationLabel || e.variation) && (
                    <label className="lbr-field">
                      <span className="lbr-field__label">Variación</span>
                      <input
                        className="lbr-field__input"
                        type="text"
                        value={e.variation}
                        onChange={(ev) => setField(r.key, 'variation', ev.target.value)}
                        placeholder={r.variationLabel || ''}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="lbr-footer">
          <button className="lbr-btn lbr-btn--soft" onClick={onCancel} disabled={generating}>
            Cancelar
          </button>
          <button className="lbr-btn lbr-btn--primary" onClick={handleConfirm} disabled={generating}>
            {generating
              ? <><span className="spinner spinner-sm" /> Generando…</>
              : 'Generar PDF con todas las etiquetas'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
